import { createContext, type ComponentChildren } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';

// The one shared notice (decisions doc › Undo notices): a small light pill pinned just under
// the header, centred, with ✓, the message, Undo when there is one, and ✕. It floats over
// the page (nothing shifts), closes itself after about 8 seconds, and a new one replaces it.

export interface NoticeInput {
  message: string;
  /** Puts the change back; shows Undo when given. */
  undo?: () => void;
  /** ✓ for a change that was made (default); off for a plain note. */
  done?: boolean;
  /** A question with one answer button (Kristi, Oct 28: "Should this book be Study?"); stays up longer. */
  action?: { label: string; run: () => void };
}

interface NoticeShown extends NoticeInput {
  id: number;
}

const NOTICE_MS = 8000;
const QUESTION_MS = 20000;
const Ctx = createContext<(n: NoticeInput) => void>(() => {});

export function useNotice() {
  return useContext(Ctx);
}

let nextId = 1;

export function NoticeProvider({ children }: { children: ComponentChildren }) {
  const [shown, setShown] = useState<NoticeShown | null>(null);

  useEffect(() => {
    if (!shown) return;
    const t = setTimeout(() => setShown((s) => (s?.id === shown.id ? null : s)), shown.action ? QUESTION_MS : NOTICE_MS);
    return () => clearTimeout(t);
  }, [shown]);

  return (
    <Ctx.Provider value={(n) => setShown({ done: true, ...n, id: nextId++ })}>
      {children}
      <div class="notice-anchor" aria-live="polite">
        {shown ? (
          <div class="notice" role="status">
            {shown.done ? (
              <span class="notice-tick" aria-hidden="true">
                ✓
              </span>
            ) : null}
            <span class="notice-text">{shown.message}</span>
            {shown.action ? (
              <button
                type="button"
                class="notice-undo notice-action"
                onClick={() => {
                  const act = shown.action;
                  setShown(null);
                  act?.run();
                }}
              >
                {shown.action.label}
              </button>
            ) : null}
            {shown.undo ? (
              <button
                type="button"
                class="notice-undo"
                onClick={() => {
                  const undo = shown.undo;
                  setShown(null);
                  undo?.(); // may show its own notice ("…is back"), which then stays
                }}
              >
                Undo
              </button>
            ) : null}
            <button type="button" class="notice-close" aria-label="Dismiss" onClick={() => setShown(null)}>
              ✕
            </button>
          </div>
        ) : null}
      </div>
    </Ctx.Provider>
  );
}
